import React, { useEffect, useMemo, useState } from 'react'
import { Box, TextField, Typography, Pagination, MenuItem, Select, Drawer, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelApi } from './api'
import { intelColors, formatNumber } from './components/intelTheme'

const STATUS_OPTIONS = ['', 'COMPLETED', 'PARTIAL', 'FAILED']
const ACTION_TYPE_OPTIONS = ['', 'BULK_UPLOAD', 'L1_TOGGLE', 'L1_SCOPE_UPDATE', 'DEACTIVATE']

const statusBadge = (s) => {
  const map = {
    COMPLETED: 'green',
    PARTIAL: 'orange',
    FAILED: 'red',
  }
  return <Badge color={map[s] || 'grey'}>{s || '—'}</Badge>
}

export default function CoverageUploads() {
  const [filters, setFilters] = useState({
    parentCp: '',
    accountCode: '',
    actionType: '',
    status: '',
    uploadedBy: '',
    from: '',
    to: '',
  })
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerCtx, setDrawerCtx] = useState(null)
  const [events, setEvents] = useState(null)
  const [evtLoading, setEvtLoading] = useState(false)
  const [evtPage, setEvtPage] = useState(0)
  // Dropdown option lists — parent loads once, courier codes cascade off the chosen parent.
  const [parentOptions, setParentOptions] = useState([])
  const [accountCodeOptions, setAccountCodeOptions] = useState([])

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .coverageUploads({ ...filters, page, size })
      .then((res) => !cancel && setData(res?.data?.responseObj || res?.data?.data || res?.data))
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [filters, page, size])

  // Parent list for the dropdown (one call on mount).
  useEffect(() => {
    let cancel = false
    intelApi
      .formOptions()
      .then((res) => {
        if (cancel) return
        const o = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setParentOptions(o.parentCps || [])
      })
      .catch(() => {})
    return () => {
      cancel = true
    }
  }, [])

  // Courier codes cascade off the chosen parent.
  useEffect(() => {
    if (!filters.parentCp) {
      setAccountCodeOptions([])
      return undefined
    }
    let cancel = false
    intelApi
      .accountCodes({ parent_cp: filters.parentCp })
      .then((res) => {
        if (cancel) return
        const b = res?.data?.responseObj || res?.data?.data || res?.data || {}
        setAccountCodeOptions((b.accountCodes || []).map((c) => c.accountCode || c).filter(Boolean))
      })
      .catch(() => !cancel && setAccountCodeOptions([]))
    return () => {
      cancel = true
    }
  }, [filters.parentCp])

  useEffect(() => {
    if (!drawerOpen || !drawerCtx) return
    let cancel = false
    setEvtLoading(true)
    intelApi
      .coverageUploadsPincodeEvents({
        accountCode: drawerCtx.accountCode,
        parentCp: drawerCtx.parent,
        pincodeType: drawerCtx.pincodeType,
        page: evtPage,
        size: 50,
      })
      .then((res) => !cancel && setEvents(res?.data?.responseObj || res?.data?.data || res?.data))
      .finally(() => !cancel && setEvtLoading(false))
    return () => {
      cancel = true
    }
  }, [drawerOpen, drawerCtx, evtPage])

  const kpis = data?.kpis || {}
  const rows = data?.rows || []

  const columns = useMemo(
    () => [
      {
        key: 'when',
        header: 'When',
        render: (r) => <span style={{ fontSize: 11.5 }}>{(r.uploadedAt || '').replace('T', ' ')}</span>,
      },
      { key: 'code', header: 'Courier Code', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3 }}>{r.accountCode}</code> },
      { key: 'parent', header: 'Parent Courier', render: (r) => r.parent },
      { key: 'type', header: 'Pincode Type', render: (r) => <Badge color={r.pincodeType === 'DROP_COD' ? 'orange' : r.pincodeType === 'PICKUP_PREPAID' ? 'blue' : 'grey'}>{r.pincodeType?.replace('_', ' ')}</Badge> },
      { key: 'action', header: 'Action', render: (r) => <Badge color={r.actionLabel === 'Deletion' ? 'red' : r.actionLabel === 'Addition' ? 'green' : 'orange'}>{r.actionLabel}</Badge> },
      { key: 'actionType', header: 'Source', render: (r) => <span style={{ fontSize: 11, color: '#555' }}>{r.actionType?.replace('_', ' ')}</span> },
      { key: 'by', header: 'By', render: (r) => r.uploadedBy },
      { key: 'count', header: 'Pincodes', render: (r) => formatNumber(r.pincodeCount) },
      { key: 'added', header: 'Added', render: (r) => (r.pincodesAdded !== null && r.pincodesAdded !== undefined ? <span style={{ color: intelColors.green, fontWeight: 600 }}>+{formatNumber(r.pincodesAdded)}</span> : '—') },
      { key: 'removed', header: 'Removed', render: (r) => (r.pincodesRemoved ? <span style={{ color: intelColors.red, fontWeight: 600 }}>-{formatNumber(r.pincodesRemoved)}</span> : '—') },
      { key: 'status', header: 'Status', render: (r) => statusBadge(r.status) },
      {
        key: 'actions',
        header: 'Actions',
        render: (r) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box
              onClick={() => {
                setDrawerCtx({ accountCode: r.accountCode, parent: r.parent, pincodeType: r.pincodeType, fileName: r.fileName })
                setEvtPage(0)
                setDrawerOpen(true)
              }}
              sx={{
                border: `1px solid ${intelColors.primary}`,
                color: intelColors.primary,
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: 10.5,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { background: '#e3f2fd' },
              }}
            >
              View Pincodes
            </Box>
            {r.s3Url && (
              <a href={r.s3Url} target='_blank' rel='noreferrer' style={{ textDecoration: 'none' }}>
                <Box sx={{ border: '1px solid #cfd8dc', color: '#37474f', borderRadius: '4px', padding: '2px 8px', fontSize: 10.5, fontWeight: 600 }}>⬇️ File</Box>
              </a>
            )}
          </Box>
        ),
      },
    ],
    []
  )

  const setF = (k) => (e) => {
    setPage(0)
    setFilters((f) => ({ ...f, [k]: e.target.value }))
  }

  return (
    <IntelLayout title='Upload History'>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='blue' label='Total Uploads (30d)' value={formatNumber(kpis.totalUploads30d?.value)} sub={kpis.totalUploadsAllTime ? `All time: ${formatNumber(kpis.totalUploadsAllTime.value)}` : ''} />
        <KpiCard loading={loading} color='green' label='Pincodes Uploaded (30d)' value={formatNumber(kpis.totalPincodesUploaded30d?.value)} sub={kpis.activeCodes30d ? `${formatNumber(kpis.activeCodes30d.value)} active codes` : ''} />
        <KpiCard loading={loading} color='red' label='Failed Uploads (30d)' value={formatNumber(kpis.failedUploads30d?.value)} />
        <KpiCard loading={loading}
          color='orange'
          label='Current Month Net'
          value={`+${formatNumber(kpis.pincodesAddedCurrentMonth?.value)} / -${formatNumber(kpis.pincodesRemovedCurrentMonth?.value)}`}
          sub={kpis.totalUploadsCurrentMonth ? `${formatNumber(kpis.totalUploadsCurrentMonth.value)} uploads this month` : ''}
        />
      </Box>

      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          padding: '14px 18px',
          mb: 2,
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-end',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Parent Courier</Typography>
          <Select
            size='small'
            displayEmpty
            value={filters.parentCp}
            onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, parentCp: e.target.value, accountCode: '' })) }}
            sx={{ width: 170, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}
          >
            <MenuItem value=''>All</MenuItem>
            {parentOptions.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: 12 }}>{p}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Courier Code</Typography>
          <Select
            size='small'
            displayEmpty
            disabled={!filters.parentCp}
            value={filters.accountCode}
            onChange={setF('accountCode')}
            sx={{ width: 190, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }}
          >
            <MenuItem value=''>{filters.parentCp ? 'All' : 'Select a parent first'}</MenuItem>
            {accountCodeOptions.map((c) => (
              <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Action Type</Typography>
          <Select size='small' value={filters.actionType} onChange={setF('actionType')} sx={{ width: 170, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }} displayEmpty>
            {ACTION_TYPE_OPTIONS.map((a) => (
              <MenuItem key={a || 'all'} value={a}>{a || 'All'}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Status</Typography>
          <Select size='small' value={filters.status} onChange={setF('status')} sx={{ width: 140, fontSize: 12, '& .MuiSelect-select': { padding: '7px 10px' } }} displayEmpty>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s || 'all'} value={s}>{s || 'All'}</MenuItem>
            ))}
          </Select>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>Uploaded By</Typography>
          <TextField size='small' value={filters.uploadedBy} onChange={setF('uploadedBy')} sx={{ width: 160, '& input': { fontSize: 12, padding: '7px 10px' } }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>From</Typography>
          <TextField type='date' size='small' value={filters.from} onChange={setF('from')} sx={{ width: 150, '& input': { fontSize: 12, padding: '7px 10px' } }} InputLabelProps={{ shrink: true }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#555', textTransform: 'uppercase', mb: '4px' }}>To</Typography>
          <TextField type='date' size='small' value={filters.to} onChange={setF('to')} sx={{ width: 150, '& input': { fontSize: 12, padding: '7px 10px' } }} InputLabelProps={{ shrink: true }} />
        </Box>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      <IntelTable title={`Upload Feed (${formatNumber(data?.totalRecords)} total)`} loading={loading} columns={columns} rows={rows} rowKey={(r, i) => r.uploadId || i} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center', mt: 1 }}>
        <Select size='small' value={size} onChange={(e) => { setSize(e.target.value); setPage(0) }} sx={{ fontSize: 12, '& .MuiSelect-select': { padding: '4px 24px 4px 8px' } }}>
          {[20, 50, 100, 200].map((s) => <MenuItem key={s} value={s}>{s} / page</MenuItem>)}
        </Select>
        <Pagination
          count={data?.totalPages || 1}
          page={page + 1}
          onChange={(_, p) => setPage(p - 1)}
          size='small'
          color='primary'
        />
      </Box>

      <Drawer anchor='right' open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: 720, p: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: intelColors.text }}>Pincode Events</Typography>
            <Typography sx={{ fontSize: 11, color: intelColors.muted }}>
              {drawerCtx?.parent} · {drawerCtx?.accountCode} · {drawerCtx?.pincodeType}
            </Typography>
            {drawerCtx?.fileName && <Typography sx={{ fontSize: 11, color: intelColors.muted }}>File: {drawerCtx.fileName}</Typography>}
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
          <KpiCard loading={loading} color='blue' label='Total Events' value={formatNumber(events?.kpis?.totalEvents)} />
          <KpiCard loading={loading} color='green' label='Additions' value={formatNumber(events?.kpis?.totalAdditions)} />
          <KpiCard loading={loading} color='red' label='Removals' value={formatNumber(events?.kpis?.totalRemovals)} />
        </Box>
        <IntelTable
          loading={evtLoading}
          columns={[
            { key: 'pin', header: 'Pincode', render: (r) => <strong>{r.pincode}</strong> },
            { key: 'act', header: 'Action', render: (r) => <Badge color={r.action === 'ADDED' ? 'green' : 'red'}>{r.action}</Badge> },
            { key: 'prev', header: 'Prev', render: (r) => <span style={{ fontSize: 11 }}>{r.previousState}</span> },
            { key: 'new', header: 'New', render: (r) => <span style={{ fontSize: 11 }}>{r.newState}</span> },
            { key: 'at', header: 'At', render: (r) => <span style={{ fontSize: 11 }}>{(r.triggeredAt || '').replace('T', ' ')}</span> },
            { key: 'by', header: 'By', render: (r) => r.triggeredBy },
          ]}
          rows={events?.events || []}
          rowKey={(r, i) => r.auditLogId || i}
          emptyMsg='No pincode events'
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Pagination
            count={events?.totalPages || 1}
            page={evtPage + 1}
            onChange={(_, p) => setEvtPage(p - 1)}
            size='small'
            color='primary'
          />
        </Box>
      </Drawer>
    </IntelLayout>
  )
}
